import './JobQueueTable.scss';

import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import EyeIcon from 'assets/images/iPrise/Eye.svg';
import PersonAddIcon from 'assets/images/iPrise/PersonAdd.svg';
import { viewJobServiceStore } from 'mobx/job/ViewJobServiceStore';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import {
  JobAssignStatus,
  JobQueueTableType,
} from 'pages/iPriseV2/common/enum/TeamUserRoles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchList } from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

import AssignUserModal from '../AssignUserModal';
import StylishLoader from '../components/StylishLoader';

// SkillsCell component for consistent truncation and popper behavior
interface SkillsCellProps {
  skills: string[];
  onMoreClick: (event: React.MouseEvent<HTMLElement>, skills: string[]) => void;
  popperId?: string;
}

const SkillsCell: React.FC<SkillsCellProps> = ({
  skills,
  onMoreClick,
  popperId,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const allSkillsString = skills.length > 0 ? skills.join(', ') : '-';

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncation = () => {
      window.requestAnimationFrame(() => {
        if (element) {
          const isOverflowing =
            element.scrollWidth >
            Math.ceil(element.getBoundingClientRect().width) + 1;
          setIsTruncated(isOverflowing);
        }
      });
    };

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    checkTruncation();
    const timer = setTimeout(checkTruncation, 250);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [allSkillsString]);

  if (skills.length === 0) {
    return <span>-</span>;
  }

  return (
    <div className="skills-cell-container">
      <span ref={textRef} className="skills-cell-text">
        {allSkillsString}
      </span>
      {isTruncated && (
        <button
          className="more-button"
          aria-describedby={popperId}
          type="button"
          onClick={(e) => onMoreClick(e, skills)}>
          More...
        </button>
      )}
    </div>
  );
};

interface JobQueueTableProps {
  tableType: JobQueueTableType;
  status: JobAssignStatus | string;
  queryKey: string;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  popperId: string;
  isBenchQueue?: boolean;
  benchStatus?: 'published' | '';
  screenStatus?: 'closed' | 'overdue' | '';
  selectedTeam?: string;
  selectedSort?: string;
  searchTerm?: string;
  percentage?: string;
}

const JobQueueTable: React.FC<JobQueueTableProps> = ({
  tableType,
  status,
  queryKey,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  popperId,
  isBenchQueue = false,
  benchStatus,
  screenStatus,
  selectedTeam,
  selectedSort,
  searchTerm,
  percentage,
}) => {
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [skillPopperOpen, setSkillPopperOpen] = useState(false);
  const [skillPopperAnchorEl, setSkillPopperAnchorEl] =
    useState<null | HTMLElement>(null);
  const [skillPopperData, setSkillPopperData] = useState<string[]>([]);
  const [assignState, setAssignState] = useState<{
    anchorEl: HTMLElement | null;
    job: any | null;
  }>({
    anchorEl: null,
    job: null,
  });

  const navigateTo = (job: any) => {
    // Determine the base path from current location
    // e.g.,  const navigateTo = (job: any) => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';

    // Set full job object in MobX store
    viewJobServiceStore.setJobInfo({
      ...job,
    });

    if (isBenchQueue) {
      navigate(
        `${basePath}/job-post/view-job/iprise-job/${String(job.id ?? '1')}`,
        {
          state: {
            Params: {
              // jobId: String(job.id ?? '1'),
              score:
                percentage !== undefined && percentage !== null
                  ? percentage
                  : 0,
              // tableType,
            },
          },
        },
      );
    } else {
      navigate(`${basePath}/view-job/${String(job.id ?? '1')}`, {
        state: {
          Params: {
            jobId: String(job.id ?? '1'),
            score:
              percentage !== undefined && percentage !== null ? percentage : 0,
            tableType,
          },
        },
      });
    }
  };

  // Helper function to generate job URL for href
  const getJobUrl = (job: any) => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';

    const scoreParam =
      percentage !== undefined && percentage !== null ? percentage : 0;

    if (isBenchQueue) {
      return `${basePath}/job-post/view-job/iprise-job/${String(
        job.id ?? '1',
      )}${scoreParam ? `?score=${String(scoreParam)}` : ''}`;
    } else {
      return `${basePath}/view-job?score=${String(scoreParam)}`;
    }
  };

  const handleAssignClick = (
    event: React.MouseEvent<HTMLElement>,
    job: any,
  ) => {
    event.stopPropagation();
    setAssignState({
      anchorEl: event.currentTarget,
      job,
    });
  };

  const handleCloseAssignModal = () => {
    setAssignState({
      anchorEl: null,
      job: null,
    });
  };

  const { columnFilterStore } = useStore();
  const { appliedFilters } = columnFilterStore;

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('skip', String(page * rowsPerPage));
    params.append('take', String(rowsPerPage));
    if (selectedTeam) {
      params.append('teamId', selectedTeam);
    }
    if (isBenchQueue) {
      params.append('sortby', '');
    } else if (selectedSort) {
      params.append('sortby', selectedSort);
    }

    if (isBenchQueue) {
      params.append('sourceQueue', 'benchQueue');
      params.append('isBenchQueue', 'true');
      params.append('isApply', 'true');
      if (selectedSort === 'oldest_first') {
        params.append('order', 'asc');
      } else {
        params.append('order', 'desc');
      }

      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      const getDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const defaultFromDate = getDateString(sevenDaysAgo);
      const defaultToDate = getDateString(today);

      params.append('createdFrom', appliedFilters.fromDate || defaultFromDate);
      params.append('createdTo', appliedFilters.toDate || defaultToDate);

      if (benchStatus) {
        params.append('status', benchStatus);
      }
      if (screenStatus === 'closed') {
        params.append('publish', 'true');
      }
      params.append('screen', screenStatus || 'all');
    } else {
      params.append('sourceQueue', 'jobQueue');
      params.append('jobAssignStatus', status);
    }

    if (percentage !== undefined && percentage !== null) {
      params.append('score', percentage || '0');
    }

    params.append('search', searchTerm || '');

    // Add Column Sidebar Filters if applied
    if (appliedFilters.applyFilter) {
      let skillId = '';
      if (appliedFilters.skillData && appliedFilters.skillData.length) {
        skillId = appliedFilters.skillData
          .map((item: any) => item.id)
          .filter(Boolean)
          .join(',');
      }

      if (appliedFilters.title) params.append('jobTitle', appliedFilters.title);
      if (appliedFilters.location)
        params.append('location', appliedFilters.location);
      if (appliedFilters.workStatus)
        params.append('workStatus', appliedFilters.workStatus);
      if (skillId) params.append('skillId', skillId);
      if (appliedFilters.assignedTo)
        params.append('assignedTo', appliedFilters.assignedTo);
      if (appliedFilters.assignedBy)
        params.append('assignedBy', appliedFilters.assignedBy);
      if (appliedFilters.fromDate && !isBenchQueue)
        params.append('createdFrom', appliedFilters.fromDate);
      if (appliedFilters.toDate && !isBenchQueue)
        params.append('createdTo', appliedFilters.toDate);
      if (appliedFilters.jobSourceId && appliedFilters.jobSourceId.length > 0)
        params.append('jobSourceId', appliedFilters.jobSourceId.join(','));

      if (appliedFilters.tabSearch === '1') {
        // Only send payRange params when user has explicitly moved the slider
        if (appliedFilters.payRangeChanged) {
          if (appliedFilters.payRangeFrom)
            params.append('payRangeFrom', appliedFilters.payRangeFrom);
          if (appliedFilters.payRangeTo)
            params.append('payRangeTo', appliedFilters.payRangeTo);
        }
      } else {
        if (appliedFilters.salaryFrom)
          params.append('salaryFrom', String(appliedFilters.salaryFrom));
        if (appliedFilters.salaryTo)
          params.append('salaryTo', String(appliedFilters.salaryTo));
      }
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    params.append('timezone', timezone);

    return params.toString();
  }, [
    page,
    rowsPerPage,
    status,
    searchTerm,
    isBenchQueue,
    benchStatus,
    screenStatus,
    selectedTeam,
    selectedSort,
    percentage,
    appliedFilters.applyFilter,
    appliedFilters.title,
    appliedFilters.location,
    appliedFilters.workStatus,
    appliedFilters.skillData,
    appliedFilters.assignedTo,
    appliedFilters.assignedBy,
    appliedFilters.fromDate,
    appliedFilters.toDate,
    appliedFilters.tabSearch,
    appliedFilters.payRangeChanged,
    appliedFilters.payRangeFrom,
    appliedFilters.payRangeTo,
    appliedFilters.salaryFrom,
    appliedFilters.salaryTo,
    appliedFilters.jobSourceId,
  ]);

  const {
    data: jobPostingsResp,
    isLoading,
    refetch,
  } = useFetchList<any>(
    [
      queryKey,
      status,
      isBenchQueue,
      benchStatus,
      screenStatus,
      page,
      rowsPerPage,
      searchTerm,
      selectedTeam,
      selectedSort,
      percentage,
      appliedFilters.applyFilter,
      appliedFilters.title,
      appliedFilters.location,
      appliedFilters.workStatus,
      appliedFilters.skillData,
      appliedFilters.assignedTo,
      appliedFilters.assignedBy,
      appliedFilters.fromDate,
      appliedFilters.toDate,
      appliedFilters.tabSearch,
      appliedFilters.payRangeChanged,
      appliedFilters.payRangeFrom,
      appliedFilters.payRangeTo,
      appliedFilters.salaryFrom,
      appliedFilters.salaryTo,
      appliedFilters.jobSourceId,
    ],
    `${MASTER_ENDPOINT.Jobs}/${String(
      MASTER_ENDPOINT.JobQueue,
    )}?${queryParams}`,
    {
      enabled: true,
      refetchOnMount: 'always',
    },
  );

  const jobPostings = useMemo(
    () => jobPostingsResp?.data || [],
    [jobPostingsResp],
  );
  const totalCount = jobPostingsResp?.total || 0;

  const getStatusChipClass = (color: string) => {
    return `status-chip status-${color}`;
  };

  const handleSkillMoreClick = (
    event: React.MouseEvent<HTMLElement>,
    skills: string[],
  ) => {
    const target = event.currentTarget;
    if (skillPopperOpen && skillPopperAnchorEl === target) {
      setSkillPopperOpen(false);
    } else {
      setSkillPopperData(skills);
      setSkillPopperAnchorEl(target);
      setSkillPopperOpen(true);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const currentPopperId = skillPopperOpen ? popperId : undefined;

  return (
    <>
      <Box className="table-main">
        <TableContainer className="teams-table-container">
          <Table stickyHeader className="teams-table">
            <TableHead className="table-header">
              <TableRow>
                <TableCell className="header-cell vendor-cell">
                  Vendor
                </TableCell>
                {!isBenchQueue && (
                  <TableCell className="header-cell">Beeline Id</TableCell>
                )}
                <TableCell className="header-cell job-title-cell">
                  Job Title
                </TableCell>
                <TableCell className="header-cell">Top Skills</TableCell>
                <TableCell className="header-cell">EXP</TableCell>
                <TableCell className="header-cell">Work Auth</TableCell>
                <TableCell className="header-cell">Bill</TableCell>
                <TableCell className="header-cell">Location</TableCell>
                <TableCell className="header-cell">Work Mode</TableCell>
                <TableCell className="header-cell">Job Type</TableCell>
                <TableCell className="header-cell">Status</TableCell>
                <TableCell className="header-cell" align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="table-body">
              {isLoading ? (
                <TableRow className="loading-row">
                  <TableCell
                    colSpan={isBenchQueue ? 11 : 12}
                    align="center"
                    className="body-cell">
                    <StylishLoader
                      mode="table"
                      text="Loading job postings..."
                    />
                  </TableCell>
                </TableRow>
              ) : jobPostings.length === 0 ? (
                <TableRow className="empty-row">
                  <TableCell
                    colSpan={isBenchQueue ? 11 : 12}
                    align="center"
                    className="body-cell">
                    <Box className="empty-table-message">
                      No job postings found.
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                jobPostings.map((job: any) => (
                  <TableRow
                    key={job.id}
                    className="table-row"
                    component="a"
                    href={getJobUrl(job)}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      navigateTo(job);
                    }}
                    sx={{ cursor: 'pointer', textDecoration: 'none' }}>
                    <TableCell className="body-cell vendor-cell">
                      <Tooltip
                        title={job.customer?.name || '-'}
                        placement="bottom-start"
                        arrow>
                        <span
                          className={`vendor-text ${
                            job.customer?.isTemporary ? 'is-temporary' : ''
                          }`}>
                          {job.customer?.name || '-'}
                        </span>
                      </Tooltip>
                    </TableCell>
                    {!isBenchQueue && (
                      <TableCell className="body-cell">
                        {job.beelineJobPostingNumber || '-'}
                      </TableCell>
                    )}
                    <TableCell className="body-cell job-title-cell">
                      <Tooltip
                        title={job.jobTitle || '-'}
                        placement="bottom-start"
                        arrow>
                        <span className="job-title-text">
                          {job.jobTitle || '-'}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="body-cell skill-cell">
                      <SkillsCell
                        skills={
                          job.jobSkills
                            ?.map((s: any) => s.skill?.name)
                            .filter(Boolean) || []
                        }
                        onMoreClick={handleSkillMoreClick}
                        popperId={currentPopperId}
                      />
                    </TableCell>
                    <TableCell className="body-cell">
                      {(() => {
                        const from = Number(job?.expFrom);
                        const to = Number(job?.expTo);
                        if (from > 0 && to > 0)
                          return String(from) + ' - ' + String(to);
                        if (from > 0) return String(from);
                        if (to > 0) return String(to);
                        return '-';
                      })()}
                    </TableCell>
                    <TableCell className="body-cell work-auth-cell">
                      <SkillsCell
                        skills={
                          job.jobWorkStatus
                            ?.map((ws: any) => ws.workStatus?.name)
                            .filter(Boolean) || []
                        }
                        onMoreClick={handleSkillMoreClick}
                        popperId={currentPopperId}
                      />
                    </TableCell>
                    <TableCell className="body-cell">
                      {(() => {
                        const from = Number(job?.payRangeFrom);
                        const to = Number(job?.payRangeTo);
                        if (from > 0 && to > 0)
                          return '$' + String(from) + ' - $' + String(to);
                        if (from > 0) return '$' + String(from);
                        if (to > 0) return '$' + String(to);
                        return '-';
                      })()}
                    </TableCell>
                    <TableCell className="body-cell">
                      {(() => {
                        const city = job.city?.trim();
                        const state = job.state?.trim();
                        if (city && state)
                          return String(city) + ', ' + String(state);
                        return String(city || '') || String(state || '') || '-';
                      })()}
                    </TableCell>
                    <TableCell className="body-cell">
                      {job.workType === 'Remote'
                        ? 'R'
                        : job.workType === 'Onsite'
                        ? 'O/S'
                        : job.workType === 'Hybrid'
                        ? 'H'
                        : '-'}
                    </TableCell>
                    <TableCell className="body-cell">
                      {job.positionType === 'Contract to Hire'
                        ? 'C2H'
                        : job.positionType === 'Direct Hire'
                        ? 'DH'
                        : job.positionType === 'Contract'
                        ? 'C'
                        : '-'}
                    </TableCell>
                    <TableCell className="body-cell">
                      <Tooltip title={job?.candidateMatchStatus || ''} arrow>
                        <Chip
                          label={
                            job.candidateMatchStatus === 'inProgress' ? (
                              <HourglassEmptyIcon
                                sx={{
                                  '@keyframes flip': {
                                    '0%, 100%': {
                                      transform: 'rotate(0deg)',
                                    },
                                    '50%': {
                                      transform: 'rotate(180deg)',
                                    },
                                  },
                                  animation: 'flip 2s ease-in-out infinite',
                                  fontSize: '14px',
                                }}
                              />
                            ) : (
                              String(job.totalCandidatesSubmitted || 0) +
                              '/' +
                              String(job.totalMatchedCandidates || 0)
                            )
                          }
                          className={getStatusChipClass(
                            job.candidateMatchStatus === 'submitted'
                              ? 'green'
                              : Number(job.totalMatchedCandidates) > 0
                              ? 'orange'
                              : job.candidateMatchStatus === 'inProgress'
                              ? 'blue'
                              : 'red',
                          )}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell className="body-cell" align="right">
                      <Box
                        className="action-buttons"
                        onClick={(e) => e.stopPropagation()}>
                        {(tableType ===
                          JobQueueTableType.BUSINESS_HEAD_UNALLOCATED ||
                          tableType === JobQueueTableType.MANAGER_UNALLOCATED ||
                          tableType === JobQueueTableType.TEAM_LEAD_NEW ||
                          tableType ===
                            JobQueueTableType.MANAGEMENT_JQ_UNALLOCATED ||
                          tableType === JobQueueTableType.SUPPORT_TL_NEW) && (
                          <IconButton
                            size="small"
                            className="action-btn"
                            onClick={(e) => handleAssignClick(e, job)}>
                            <img src={PersonAddIcon} alt="Add" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          className="action-btn"
                          onClick={(e: any) => {
                            e.preventDefault();
                            navigateTo(job);
                          }}>
                          <img src={EyeIcon} alt="View" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        className="teams-pagination"
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[50, 100, 150, 200, 250]}
        onPageChange={(_e, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) =>
          onRowsPerPageChange(parseInt(e.target.value, 10))
        }
        labelDisplayedRows={({ from, to, count }) =>
          String(from) + ' to ' + String(to) + ' of ' + count.toLocaleString()
        }
      />

      <Popper
        anchorEl={skillPopperAnchorEl}
        id={currentPopperId}
        open={skillPopperOpen}
        style={{ zIndex: 1300 }}
        placement="bottom-start"
        disablePortal={false}>
        <ClickAwayListener onClickAway={() => setSkillPopperOpen(false)}>
          <Box className="skills-popper-content">
            {skillPopperData.join(', ')}
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* Assign User Modal */}
      {assignState.job && (
        <AssignUserModal
          open={Boolean(assignState.anchorEl)}
          onClose={handleCloseAssignModal}
          refetch={refetch}
          anchorEl={assignState.anchorEl}
          userDetail={userDetail}
          jobData={assignState.job}
        />
      )}
    </>
  );
};

export default observer(JobQueueTable);
